import { Ability } from '@casl/ability';
import {
  Schedule,
  Event,
  Subjects,
  Actions,
  ResultStatus,
  EditScheduleForm,
  editScheduleFormDefault,
  editScheduleFormSchema,
  EditEventForm,
  editEventFormDefault,
  editEventFormSchema,
  QueryResult,
  MultiSelectOption,
  ClaimOwnerCapacity,
} from '@dispatch/shared/types';
import {
  SectionDivider,
  Form,
  RenderFuncProps,
  SectionWithMeta,
  Modal,
  CreateNewEntityButton,
} from '@dispatch/web/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Stack } from '@mui/material';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Control, FieldErrors, FieldValues, useForm } from 'react-hook-form';
import { EditEvent } from './EditEvent';
import { EditSchedule } from './EditSchedule';
import { GeneralInformation } from './GeneralInformation';
import ResourceEvents from './ResourceEvents';
import ResourceSchedule from './ResourceSchedule';
import { ClaimCapacity } from './ClaimCapacity';

export type GeneralTabProps = {
  eventsQuery?: QueryResult<Event[] | undefined, unknown>;
  schedulesQuery?: QueryResult<Schedule[] | undefined, unknown>;
  upsertEventStatus: ResultStatus;
  upsertScheduleStatus: ResultStatus;
  onScheduleSave: (values: EditScheduleForm) => void;
  onEventSave: (values: EditEventForm) => void;
  control: Control<FieldValues>;
  permissions: Ability<[Actions, Subjects]> | undefined;
  errors: FieldErrors;
  isNewResource: boolean;
  efficiencies: MultiSelectOption[];
  claimOwnerCapacity?: ClaimOwnerCapacity;
  isAlwaysAvailable?: boolean;
  isReadOnly?: boolean;
};

export function GeneralTab({
  eventsQuery,
  schedulesQuery,
  upsertEventStatus,
  upsertScheduleStatus,
  onScheduleSave,
  onEventSave,
  control,
  permissions,
  errors,
  isNewResource,
  efficiencies,
  claimOwnerCapacity,
  isAlwaysAvailable,
  isReadOnly = false,
}: GeneralTabProps) {
  const {
    control: editScheduleControl,
    formState: editScheduleFormState,
    handleSubmit: handleEditScheduleSubmit,
    reset: resetEditSchedule,
  } = useForm<EditScheduleForm>({
    defaultValues: {
      ...editScheduleFormDefault,
    },
    resolver: yupResolver(editScheduleFormSchema),
  });

  const {
    control: editEventControl,
    formState: editEventFormState,
    handleSubmit: handleEditEventSubmit,
    reset: resetEditEvent,
    watch: watchEditEvent,
    getValues: getEditValues,
    setValue: setEditValue,
  } = useForm<EditEventForm>({
    defaultValues: {
      ...editEventFormDefault,
    },
    resolver: yupResolver(editEventFormSchema),
  });
  const hasUpsertSchedulesPermissions = permissions?.can?.(
    'upsert',
    'resource-schedules'
  );
  const hasUpsertEventsPermissions = permissions?.can?.(
    'upsert',
    'resource-events'
  );

  const handleEditEventSave = useCallback(() => {
    handleEditEventSubmit(onEventSave)();
  }, [handleEditEventSubmit, onEventSave]);
  const handleEditScheduleSave = useCallback(() => {
    handleEditScheduleSubmit(onScheduleSave)();
  }, [handleEditScheduleSubmit, onScheduleSave]);

  const [openEditSchedule, setOpenEditSchedule] = React.useState(false);
  const handleOpenEditSchedule = useCallback(() => {
    setIsEdit(false);
    resetEditSchedule(editScheduleFormDefault);
    setOpenEditSchedule(true);
  }, [resetEditSchedule]);

  const handleCloseEditSchedule = () => {
    setOpenEditSchedule(false);
  };

  const [openEditEvent, setOpenEditEvent] = React.useState(false);

  const handleOpenEditEvent = useCallback(() => {
    setIsEdit(false);
    resetEditEvent(editEventFormDefault);
    setOpenEditEvent(true);
  }, [resetEditEvent]);

  const handleCloseEditEvent = () => {
    setOpenEditEvent(false);
  };

  const [isEdit, setIsEdit] = React.useState(false); // if the user is editing event/schedule

  useEffect(() => {
    if (upsertScheduleStatus === 'success') {
      resetEditSchedule();
      handleCloseEditSchedule();
    }
  }, [upsertScheduleStatus, resetEditSchedule]);

  useEffect(() => {
    if (upsertEventStatus === 'success') {
      resetEditEvent();
      handleCloseEditEvent();
    }
  }, [upsertEventStatus, resetEditEvent]);

  const setEditEventForm = useCallback(
    (editEventForm: EditEventForm) => {
      resetEditEvent(editEventForm);
    },
    [resetEditEvent]
  );

  const setEditScheduleForm = useCallback(
    (editScheduleForm: EditScheduleForm) => {
      resetEditSchedule(editScheduleForm);
    },
    [resetEditSchedule]
  );

  const renderWorkdaySection = useCallback(() => {
    return (
      <SectionWithMeta
        title="Work Days"
        meta={() => (
          <Box hidden={!hasUpsertSchedulesPermissions}>
            <CreateNewEntityButton
              onClick={handleOpenEditSchedule}
              entityName="Schedule"
              testId="edit-schedule-button"
              disabled={isNewResource || isReadOnly}
            />
          </Box>
        )}
      >
        <Modal
          testId="cert-popper-schedule"
          open={openEditSchedule}
          handleClickAway={handleCloseEditSchedule}
          title={'Work Days'}
        >
          <Form<EditScheduleForm>
            formId={'editScheduleForm'}
            errors={editScheduleFormState.errors}
            control={editScheduleControl}
            handleSubmit={handleEditScheduleSubmit}
            render={({ control, errors }: RenderFuncProps) => {
              return (
                <EditSchedule
                  control={control}
                  errors={errors}
                  onSubmit={handleEditScheduleSave}
                  upsertScheduleStatus={upsertScheduleStatus}
                  isEdit={isEdit}
                  permissions={permissions}
                />
              );
            }}
          />
        </Modal>
        <ResourceSchedule
          schedulesQuery={schedulesQuery}
          permissions={permissions}
          setOpenEditSchedule={setOpenEditSchedule}
          setEditScheduleForm={setEditScheduleForm}
          setIsEdit={setIsEdit}
          isReadOnly={isReadOnly}
        />
      </SectionWithMeta>
    );
  }, [
    editScheduleControl,
    editScheduleFormState.errors,
    schedulesQuery,
    handleEditScheduleSave,
    handleEditScheduleSubmit,
    handleOpenEditSchedule,
    hasUpsertSchedulesPermissions,
    isEdit,
    isNewResource,
    openEditSchedule,
    permissions,
    setEditScheduleForm,
    upsertScheduleStatus,
  ]);

  const renderBlockedTimeSection = useCallback(() => {
    return (
      <SectionWithMeta
        title="Blocked Time"
        meta={() => (
          <Box hidden={!hasUpsertEventsPermissions}>
            <CreateNewEntityButton
              onClick={handleOpenEditEvent}
              entityName="Event"
              testId="edit-event-button"
              disabled={isNewResource || isReadOnly}
            />
          </Box>
        )}
      >
        <Modal
          testId="cert-popper"
          open={openEditEvent}
          handleClickAway={handleCloseEditEvent}
          title={'Blocked Time'}
        >
          <Form<EditEventForm>
            formId={'editEventForm'}
            errors={editEventFormState.errors}
            control={editEventControl}
            handleSubmit={handleEditEventSubmit}
            render={({ control, errors }: RenderFuncProps) => {
              return (
                <EditEvent
                  control={control}
                  errors={errors}
                  onSubmit={handleEditEventSave}
                  upsertEventStatus={upsertEventStatus}
                  isEdit={isEdit}
                  watchEditEvent={watchEditEvent}
                  getEditValues={getEditValues}
                  setEditValues={setEditValue}
                />
              );
            }}
          />
        </Modal>
        <ResourceEvents
          eventsQuery={eventsQuery}
          permissions={permissions}
          setOpenEditEvent={setOpenEditEvent}
          setEditEventForm={setEditEventForm}
          setIsEdit={setIsEdit}
          isReadOnly={isReadOnly}
        />
      </SectionWithMeta>
    );
  }, [
    editEventControl,
    editEventFormState.errors,
    eventsQuery,
    handleEditEventSave,
    handleEditEventSubmit,
    handleOpenEditEvent,
    hasUpsertEventsPermissions,
    isEdit,
    isNewResource,
    openEditEvent,
    permissions,
    setEditEventForm,
    upsertEventStatus,
    watchEditEvent,
  ]);

  const renderSections = useCallback(() => {
    return (
      <>
        {!isAlwaysAvailable && (
          <>
            <SectionDivider />
            <Stack>{renderWorkdaySection()}</Stack>
          </>
        )}
        <SectionDivider />
        <Stack>{renderBlockedTimeSection()}</Stack>
      </>
    );
  }, [isAlwaysAvailable, renderBlockedTimeSection, renderWorkdaySection]);

  return (
    <div
      style={{
        padding: '24px',
      }}
    >
      <GeneralInformation
        control={control}
        permissions={permissions}
        errors={
          errors && errors['generalInformation']
            ? errors['generalInformation']
            : null
        }
        efficiencies={efficiencies}
        isReadOnly={isReadOnly}
      />
      <SectionDivider />
      <ClaimCapacity
        control={control}
        errors={errors}
        claimOwnerCapacity={claimOwnerCapacity}
        isReadOnly={isReadOnly}
      />
      {renderSections()}
    </div>
  );
}
